import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { Button, Card, Flex, Typography, message, Modal, Form, Upload, DatePicker, Alert } from "antd";
import { UploadOutlined, ShareAltOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { getJob, getJobPublic, applyToJob, candMe, getCandidateToken,
        getOrgId } from "../services/api";
import sanitizeHtml from "../utils/sanitizeHtml";
import CareersNav from "../components/CareersNav";
import dayjs from "dayjs";

const { Title, Text } = Typography;

export default function JobDetail() {
  const { id } = useParams();
  const { search, pathname } = useLocation();
  const navigate = useNavigate();

  const usp = useMemo(() => new URLSearchParams(search), [search]);
  const orgId = usp.get("orgId");
  const token = usp.get("token"); // may be NULL/empty; we won't rely on it for private calls
  const q = usp.get("q") || "";
  const page = Number(usp.get("page") || "1");

  const [org, setOrg] = useState(null);
  const [job, setJob] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [openApply, setOpenApply] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [file, setFile] = useState(null);
  const [preferredSlot, setPreferredSlot] = useState(null); // NEW

  function isAuthError(err) {
    const msg = String(err?.message || err || "").toLowerCase();
    return (
      msg.includes("401") ||
      msg.includes("unauthorized") ||
      msg.includes("403") ||
      msg.includes("forbidden")
    );
  }
  function is404(err) {
    if (!err) return false;
    const msg = String(err.message || err || "").toLowerCase();
    return msg.includes("(404)") || msg.includes("not_found") || msg.includes("not found");
  }

  // ---------- Ensure orgId is present in URL (restore from persisted helper) ----------
  useEffect(() => {
    // Use your existing helper from services/api (persisted in cookie/localStorage)
    const persistedOrgId = typeof getOrgId === "function" ? getOrgId() : null;

    // If URL missing orgId but we have a persisted one, rewrite the URL (preserve other params)
    if (!orgId && persistedOrgId) {
      const next = new URLSearchParams(usp);
      next.set("orgId", String(persistedOrgId));
      navigate(`${pathname}?${next.toString()}`, { replace: true });
    }
    // If both are missing, we let the page render; navbar/login flow can handle gating.
    
  }, [orgId, pathname, search, navigate]);
  // --------------------------------------------------------------------------

  // ---------- Data load (Private-first with public fallback) ----------
  useEffect(() => {
    let cancelled = false;
    setNotFound(false);
    setJob(null);

    (async () => {
      try {
        // Always try PRIVATE first; API layer will inject token from #hash/cookie.
        try {
          const j = await getJob({ orgId, id }); // do NOT force query-param token here
          if (cancelled) return;
          setJob(j);
          setOrg(j?.organization || null);
          return;
        } catch (err) {
          if (is404(err)) {
            if (!cancelled) setNotFound(true);
            return;
          }
          // If it's not an auth error, surface it; else fall back to public.
          if (!isAuthError(err)) {
            throw err;
          }
        }

        // PUBLIC fallback (may not include AOI/Location on some deployments)
        try {
          const jPub = await getJobPublic({ orgId, id });
          if (cancelled) return;
          setJob(jPub);
          setOrg(jPub?.organization || null);
        } catch (err) {
          if (cancelled) return;
          if (is404(err)) setNotFound(true);
          else message.error(String(err?.message || err || "Failed to load job"));
        }
      } catch (e) {
        if (!cancelled) message.error(String(e?.message || e || "Failed to load job"));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [orgId, id]); // intentionally not depending on token; we rely on API layer injection
  // ----------------------------------------------------------

  // Keep existing back link behavior
  const hash = typeof window !== "undefined" ? (window.location.hash || "") : "";
  const backHref = `/?orgId=${orgId || ""}&token=${encodeURIComponent(
    token || ""
  )}&q=${encodeURIComponent(q)}&page=${page}${hash}`;

  async function ensureSignedInThenOpenApply() {
    if (!getCandidateToken()) {
      message.info("Please sign in from the top-right account icon to apply.");
      return;
    }
    const response = await candMe();
    if (!response || !response.candidate) {
      message.info("Please sign in from the top-right account icon to apply.");
      return;
    }
    // ✅ FIX: candMe() returns { ok: true, candidate: { verified: true } }
    // So we need to check response.candidate.verified, not response.verified
    const current = response.candidate;
    console.log('[JobDetail] Current user:', current);
    
    if (!current.verified) {
      message.warning("Note: Your email is not verified. Please check your inbox to verify your email.", 5);
      // Allow them to proceed with application anyway
    }
    setOpenApply(true);
  }

  // Disable dates earlier than 24h from now — and disable early times on the min day
  const disabledDate = (current) => {
    if (!current) return false;
    const min = dayjs().add(24, "hour").startOf("day");
    // Disallow past days and the min-day before current hour/minute will be handled in disabledTime
    return current.isBefore(min, "day");
  };
  const disabledTime = (current) => {
    const min = dayjs().add(24, "hour");
    if (!current) return {};
    if (!current.isSame(min, "day")) return {};
    // If user picks the min-day, disable hours < min.hour, and minutes < min.minute when same hour.
    const disabledHours = () => Array.from({ length: min.hour() }, (_, i) => i);
    const disabledMinutes = () =>
      current.hour() === min.hour()
        ? Array.from({ length: min.minute() }, (_, i) => i)
        : [];
    return { disabledHours, disabledMinutes };
  };

  const uploadProps = {
    beforeUpload: (f) => {
      setFile(f);
      return false;
    },
    onRemove: () => setFile(null),
    multiple: false,
    accept: ".pdf,.doc,.docx",
    maxCount: 1,
  };

  async function submitApplication() {
    try {
      if (!file) {
        message.error("Upload your resume file");
        return;
      }
      if (!preferredSlot) {
        message.error("Pick a preferred interview time");
        return;
      }
      const min = dayjs().add(24, "hour");
      if (preferredSlot.isBefore(min)) {
        message.error("Interview must be at least 24h from now");
        return;
      }

      setSubmitting(true);
      // Send the ISO string in UTC (DatePicker+dayjs value is local; toISOString converts to UTC)
      const resp = await applyToJob({
        orgId,
        id,
        file,
        preferredInterviewUtc: preferredSlot.toDate().toISOString(),
      });
      if (resp?.alreadyApplied)
        message.info(resp.message || "You have already applied for this job");
      else
        message.success(
          resp?.message ||
            "Your application was submitted. You'll receive an email confirming the interview details."
        );
      setOpenApply(false);
      setFile(null);
      setPreferredSlot(null);
    } catch (e) {
      message.error(String(e.message || e));
    } finally {
      setSubmitting(false);
    }
  }

  // -------- Share helpers --------
  function buildShareUrl() {
    const jid = encodeURIComponent(id);
    try {
      const ref = document.referrer ? new URL(document.referrer) : null;
      if (ref && ref.origin) {
        return `${ref.origin.replace(/\/+$/, "")}/careers/?jid=${jid}`;
      }
    } catch {
      /* ignore and use fallback */
    }
    return `https://encreez.com/careers/?jid=${jid}`;
  }

  async function onShareClick() {
    const shareUrl = buildShareUrl();
    const title = job?.job_req_title || "Job Opportunity";
    const text = `Check out this job: ${title}`;

    if (navigator.share) {
      try {
        await navigator.share({ title, text, url: shareUrl });
        return;
      } catch {
        // ignore
      }
    }

    let acked = false;
    function onAck(e) {
      try {
        if (e && e.data && e.data.type === "rizqira:share:ack") {
          acked = true;
          window.removeEventListener("message", onAck);
        }
      } catch (_e) {}
    }
    window.addEventListener("message", onAck);

    try {
      window.parent &&
        window.parent.postMessage(
          { type: "rizqira:share", title, text, url: shareUrl },
          "*"
        );
    } catch {
      /* ignore */
    }

    setTimeout(() => {
      if (acked) return;
      try {
        const temp = document.createElement("input");
        temp.value = shareUrl;
        document.body.appendChild(temp);
        temp.select();
        document.execCommand("copy");
        document.body.removeChild(temp);
        message.success("The share link is copied to your clipboard");
      } catch {
        message.info("Please copy the link from your browser’s address bar.");
      }
      window.removeEventListener("message", onAck);
    }, 400);
  }
  // --------------------------------

  // ---------- Not Found (friendly) ----------
  if (notFound) {
    return (
      <>
        <CareersNav org={org} orgId={orgId} token={token} />
        <div className="page-wrap" style={{ paddingTop: 24, paddingBottom: 40 }}>
          <Link to={backHref} className="app-link">
            <ArrowLeftOutlined /> Back to search
          </Link>

          <Card style={{ marginTop: 24, borderColor: "#eee" }}>
            <div style={{ display: "flex", gap: 24, alignItems: "center", flexWrap: "wrap" }}>
              <svg
                width="140"
                height="140"
                viewBox="0 0 140 140"
                role="img"
                aria-label="Not found"
                style={{ flex: "0 0 auto" }}
              >
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#e5f0ff" />
                    <stop offset="100%" stopColor="#f3f7ff" />
                  </linearGradient>
                </defs>
                <circle cx="70" cy="70" r="68" fill="url(#g1)" stroke="#e6eef7" />
                <g transform="translate(35,35)" stroke="#98a2b3" fill="none" strokeWidth="2.5">
                  <rect x="0" y="0" width="50" height="40" rx="6" />
                  <line x1="7" y1="12" x2="43" y2="12" />
                  <line x1="7" y1="20" x2="35" y2="20" />
                  <line x1="7" y1="28" x2="28" y2="28" />
                  <circle cx="62" cy="50" r="12" />
                  <line x1="70" y1="58" x2="82" y2="70" />
                  <line x1="58" y1="42" x2="66" y2="50" />
                </g>
              </svg>

              <div style={{ minWidth: 260 }}>
                <Title level={3} style={{ marginBottom: 8 }}>Page not found</Title>
                <Text type="secondary">
                  The job reference you followed appears to be invalid or no longer available.
                </Text>

                <div style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Button onClick={() => navigate(-1)}>Go back</Button>
                  <Link to={`/${search}`}>
                    <Button type="primary">Browse all jobs</Button>
                  </Link>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </>
    );
  }
  // -------------------------------------------

  if (!job) return (
    <>
      <CareersNav org={org} orgId={orgId} token={token} />
      <div className="page-wrap" style={{ paddingTop: 24, paddingBottom: 40 }}>
        <Link to={backHref} className="app-link">
          <ArrowLeftOutlined /> Back to search
        </Link>
        <Card style={{ marginTop: 24, borderColor: "#eee" }}>
          <Text type="secondary">Loading job details…</Text>
        </Card>
      </div>
    </>
  );

  const safeDescriptionHtml = sanitizeHtml(job.job_req_description);

  return (
    <>
      <CareersNav org={org} orgId={orgId} token={token} />

      <div className="page-wrap">
        <a href={backHref} className="app-link">
          &lt; Back to search results
        </a>

        <Flex align="center" gap={12} style={{ margin: "22px 0 12px" }}>
          <Title level={2} style={{ margin: 0 }}>
            {job.job_req_title}
          </Title>
          <Button
            type="primary"
            shape="circle"
            size="middle"
            icon={<ShareAltOutlined />}
            aria-label="Share job"
            onClick={onShareClick}
          />
        </Flex>

        <Card style={{ background: "#f5f5f5", borderColor: "#eee", marginBottom: 36 }}>
          <div className="job-info-grid">
            <div className="job-info-left">
              <div>
                <Text strong>JOB ID:</Text>
                <div>{job.job_req_id}</div>
              </div>
              <div>
                <Text strong>AREA OF INTEREST:</Text>
                <div>{job.area_of_interest ?? ""}</div>
              </div>
            </div>
            <div className="job-info-right">
              <div style={{ width: "100%" }}>
                <Text strong>LOCATION:</Text>
                <div>{job.location ?? ""}</div>
              </div>
              <div style={{ width: "100%", display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <Button type="primary" size="large" onClick={ensureSignedInThenOpenApply}>
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </Card>

        <div style={{ marginTop: 44 }}>
          <div style={{ background: "#f5f5f5", padding: "16px 20px", marginBottom: "28px" }}>
            <Title level={2} style={{ margin: 0 }}>
              Job Description
            </Title>
          </div>
          <div className="job-description" dangerouslySetInnerHTML={{ __html: safeDescriptionHtml }} />
        </div>

        <Modal
          title="Submit your application"
          open={openApply}
          onCancel={() => {
            if (!submitting) setOpenApply(false);
          }}
          footer={null}
          destroyOnHidden
        >
          <Form layout="vertical" onFinish={submitApplication}>
            {/* Instructions per requirements */}
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={
                <div>
                  <div>• You can only schedule interviews <b>24h in advance</b>.</div>
                  <div>• Allow for at least <b>60min</b> of interview time (actual duration may vary).</div>
                </div>
              } 
            />
            <Form.Item label="Resume (PDF/DOC)">
              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />}>Select File</Button>
              </Upload>
            </Form.Item>
            <Form.Item label="Preferred interview time" required>
              <DatePicker
                showTime
                style={{ width: "100%" }}
                value={preferredSlot}
                onChange={(val) => setPreferredSlot(val)}
                disabledDate={disabledDate}
                disabledTime={disabledTime}
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={submitting} block>
                Submit Application
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </>
  );
}
